import {
  DateField,
  DeleteButton,
  EditButton,
  FilterDropdown,
  List,
  ShowButton,
  TextField,
  useTable,
  TagField,
} from "@refinedev/antd";
import { BaseRecord, HttpError, useTranslate } from "@refinedev/core";
import { Input, Space, Table, Card, Tag } from "antd";
import { ITenant } from "../../models/tenant";

export const TenantList = () => {
  const _t = useTranslate();

  const { tableProps } = useTable<ITenant, HttpError>({
    syncWithLocation: true,
    sorters: {
      initial: [{ field: "id", order: "asc" }],
    },
    filters: {
      initial: [
        {
          field: "name",
          operator: "contains",
          value: "",
        },
      ],
    },
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "green";
      case "suspended":
        return "red";
      case "trial":
        return "blue";
      default:
        return "default";
    }
  };

  const getSubscriptionColor = (subscriptionStatus: string) => {
    switch (subscriptionStatus) {
      case "active":
        return "green";
      case "trial":
        return "blue";
      case "free":
        return "default";
      default:
        return "default";
    }
  };

  return (
    <List>
      <Card className="shadow-lg">
        <Table {...tableProps} rowKey="id">
          <Table.Column dataIndex="id" title="ID" sorter={{ multiple: 2 }} />
          <Table.Column
            dataIndex="name"
            title={_t("tenants.fields.name", "Name")}
            sorter={{ multiple: 1 }}
            filterDropdown={(props) => (
              <FilterDropdown {...props}>
                <Input placeholder={_t("tenants.fields.name", "Name")} />
              </FilterDropdown>
            )}
          />
          <Table.Column
            dataIndex="slug"
            title={_t("tenants.fields.slug", "Slug")}
            render={(value: any) => <TextField value={value} />}
          />
          <Table.Column
            dataIndex="status"
            title={_t("tenants.fields.status", "Status")}
            render={(value: any) => (
              <Tag color={getStatusColor(value)}>
                {_t(`tenants.fields.statusValues.${value}`, value)}
              </Tag>
            )}
            width={120}
            filters={[
              {
                text: _t("tenants.fields.statusValues.active", "Active"),
                value: "active",
              },
              {
                text: _t("tenants.fields.statusValues.suspended", "Suspended"),
                value: "suspended",
              },
              {
                text: _t("tenants.fields.statusValues.trial", "Trial"),
                value: "trial",
              },
            ]}
            onFilter={(value: any, record: any) => record.status === value}
          />
          <Table.Column
            dataIndex="subscriptionStatus"
            title={_t("tenants.fields.subscriptionStatus", "Subscription")}
            render={(value: any) =>
              value ? (
                <Tag color={getSubscriptionColor(value)}>
                  {_t(`tenants.fields.subscriptionStatusValues.${value}`, value)}
                </Tag>
              ) : null
            }
            width={130}
            filters={[
              {
                text: _t("tenants.fields.subscriptionStatusValues.free", "Free"),
                value: "free",
              },
              {
                text: _t("tenants.fields.subscriptionStatusValues.trial", "Trial"),
                value: "trial",
              },
              {
                text: _t("tenants.fields.subscriptionStatusValues.active", "Active"),
                value: "active",
              },
            ]}
            onFilter={(value: any, record: any) =>
              record.subscriptionStatus === value
            }
          />
          <Table.Column
            dataIndex="trialEndsAt"
            title={_t("tenants.fields.trialEndsAt", "Trial ends")}
            width={150}
            render={(value: any) => {
              if (!value) return null;
              const expired = new Date(value).getTime() <= Date.now();
              return (
                <Tag color={expired ? "red" : "default"}>
                  <DateField value={value} format="DD/MM/YYYY" />
                </Tag>
              );
            }}
          />
          <Table.Column
            dataIndex="createdAt"
            title={_t("tenants.fields.createdAt", "Created At")}
            render={(value: any) => (
              <DateField value={value} format="DD/MM/YYYY HH:mm" />
            )}
          />
          <Table.Column
            dataIndex="updatedAt"
            title={_t("tenants.fields.updatedAt", "Updated At")}
            render={(value: any) => (
              <DateField value={value} format="DD/MM/YYYY HH:mm" />
            )}
          />
          <Table.Column
            title={_t("table.actions", "Actions")}
            dataIndex="actions"
            render={(_, record: BaseRecord) => (
              <Space>
                <EditButton
                  hideText
                  size="small"
                  recordItemId={record.id}
                />
                <ShowButton
                  hideText
                  size="small"
                  recordItemId={record.id}
                />
                <DeleteButton
                  hideText
                  size="small"
                  recordItemId={record.id}
                />
              </Space>
            )}
          />
        </Table>
      </Card>
    </List>
  );
};


